import { InformationOutline18Icon } from "@vygruppen/spor-icon-react";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Box, Alert } from "shared/components/Spor";

export interface InfoIconHoverAlertProps {
  children: ReactNode;
  width?: string | number;
  alignment?: "left" | "right";
  offsetTop?: string | number;
  zIndex?: number;
  iconColor?: string;
}

export const InfoIconHoverAlert = ({
  children,
  width = "300px",
  alignment = "left",
  offsetTop = "5px",
  zIndex = 10000,
  iconColor = undefined,
}: InfoIconHoverAlertProps) => {
  const [hover, setHover] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{
    top: number;
    left?: number;
    right?: number;
  } | null>(null);
  const iconRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    if (!iconRef.current) return;

    const rect = iconRef.current.getBoundingClientRect();
    const offsetTopNum =
      typeof offsetTop === "string"
        ? parseFloat(offsetTop.replace("px", ""))
        : offsetTop;

    setTooltipPosition({
      top: rect.bottom + offsetTopNum,
      ...(alignment === "right"
        ? { right: window.innerWidth - rect.right }
        : { left: rect.left }),
    });
  }, [offsetTop, alignment]);

  useEffect(() => {
    if (!hover) {
      setTooltipPosition(null);
      return;
    }

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [hover, updatePosition]);

  return (
    <>
      <Box
        ref={iconRef}
        position="relative"
        ml="auto"
        display="flex"
        alignItems="center"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <InformationOutline18Icon color={iconColor} />
      </Box>
      {hover &&
        tooltipPosition &&
        createPortal(
          <Box
            position="fixed"
            top={`${tooltipPosition.top}px`}
            left={
              tooltipPosition.left !== undefined
                ? `${tooltipPosition.left}px`
                : undefined
            }
            right={
              tooltipPosition.right !== undefined
                ? `${tooltipPosition.right}px`
                : undefined
            }
            zIndex={zIndex + 400}
          >
            <Alert data-color="info" width={width}>
              {children}
            </Alert>
          </Box>,
          document.body,
        )}
    </>
  );
};
